import { some, each } from 'lodash';

import { redirect } from '@adjust/dashboard-core/lib/utils/location';
import { destroyAdapterCache } from '@adjust/dashboard-core/api/instance/middlewares/throttleAdapterEnhancer';
import { initHistoryEvents } from '@react/constants/historyEvent';
import { resetState, setAccount, setUser } from '@redux/ducks/global';

/**
 * Global route resolver which is used to resolved route against requirements defined for each one of them
 * Nested routes inherit resolved object from parent so it can be used for further child resolving requirements
 */
function RouteResolver(
  User,
  Account,
  $q,
  Moment,
  LanguageLoader,
  $location,
  $state,
  $uiRouterGlobals,
  $ngRedux,
  Agent,
) {
  'ngInject';

  /**
   * Available tasks used for requirements resolving
   *
   * @type {Object}
   * @private
   */
  const _tasks = {
    privatePage: _privatePageAccess,
    checkMissedPayment: _checkMissedPayment,
    paymentSetupAccess: _paymentSetupAccess,
    pricingPlanAccess: _pricingPlanAccess,
    canUseAccountSettings: _canUseAccountSettingsAccess,
    initHistoryEvents: _initHistoryEvents,

    canAccessPaymentSetup: _canAccessPaymentSetup,
    canAccessSsoConfig: _canAccessSsoConfig,

    canAccessUsersRoleSelection: _canAccessUsersRoleSelection,
  };

  /**
   * Resources with their requirements and optional child resources definitions
   *
   * @type {Object}
   * @private
   */
  const _resources = {
    accountSettings: {
      resolve: ['privatePage', 'canUseAccountSettings', 'checkMissedPayment', 'initHistoryEvents'],
      'account-data': {
        resolve: [],
        'user-details': { resolve: [] },
        'company-details': { resolve: [] },
        'payment-method': { resolve: ['canAccessPaymentSetup'] },
        'sso-configuration': { resolve: ['canAccessSsoConfig'] },
        'two-factor-authentication': { resolve: [] },
      },
      invoices: { resolve: [] },
      'account-history': { resolve: [] },
      'addons-packages': { resolve: ['pricingPlanAccess'] },
      users: {
        resolve: [],
        'about-permissions': { resolve: [] },
        'role-selection': { resolve: ['canAccessUsersRoleSelection'] },
        'create-user': { resolve: [] },
        details: { resolve: [] },
        access: { resolve: [] },
        email: { resolve: [] },
      },
      'payment-setup': { resolve: ['paymentSetupAccess'] },
    },
  };

  return {
    resolve,
  };

  function _checkMissedPayment({ params: { target } }) {
    return $q.when(Account.getCurrent()).then(currentAccount => {
      const paymentSetupState = 'accountSettings.payment-setup';

      if (target !== paymentSetupState && isThereMissedPayment(currentAccount)) {
        return $q.reject(_redirectWithAccessDenied(paymentSetupState));
      }

      return currentAccount;
    });
  }

  function isThereMissedPayment(account) {
    const missedPaymentInformation = account.missing_payment_information;
    const isDemo = account.in_demo_period;
    const isDelayedDemoPeriod = account.delayed_demo_period;
    const isRunning = account.running;
    const isDisabled = account.disabled;

    return missedPaymentInformation && !isDemo && !isDelayedDemoPeriod && !isRunning && !isDisabled;
  }

  function _paymentSetupAccess() {
    return $q.when(Account.getCurrent()).then(currentAccount => {
      if (!isThereMissedPayment(currentAccount)) {
        return $q.reject(_redirectWithAccessDenied('accountSettings'));
      }

      return currentAccount;
    });
  }

  function _pricingPlanAccess() {
    return $q.when(Account.getCurrent()).then(currentAccount => {
      if (Account.hasNewFeatureScope(currentAccount)) {
        return $q.reject(_redirectWithAccessDenied('accountSettings'));
      }

      return currentAccount;
    });
  }

  /**
   * @returns {Promise<Any>}
   * @private
   */
  function _handleUnauthorizedLoginRedirect(error) {
    if (!error || error.status === 401) {
      destroyAdapterCache();
      return $q.reject(_redirectToLogin());
    }

    return $q.when();
  }

  /**
   * Checks if user can access private pages, i.e. if user is authorized
   *
   * @returns {Promise} A promise that represent the state of current user
   * @private
   */
  function _privatePageAccess() {
    const deferred = $q.defer();

    $q.all([Account.getCurrent(), User.getCurrent()])
      .then(([currentAccount, currentUser]) => {
        $ngRedux.dispatch(setAccount(currentAccount));
        $ngRedux.dispatch(setUser(currentUser));
        Agent.setData(currentUser, currentAccount);
        $q.all([_checkAdvancedPermissions(currentUser), _checkAccountToken()])
          .then(() => {
            Moment.initialize();
            LanguageLoader.load(currentUser.language).then(() => {
              deferred.resolve(); // Continue with normal flow
            });
          })
          .catch(rejection => {
            deferred.reject(rejection); // Trigger redirection
          });
      })
      .catch(err => {
        $ngRedux.dispatch(resetState());
        _handleUnauthorizedLoginRedirect(err).catch(rejection => {
          deferred.reject(rejection); // Continue with rejection flow
        });
      });

    return deferred.promise;
  }

  /**
   * @param {Object} currentUser
   * @private
   */
  function _checkAdvancedPermissions(currentUser) {
    const deferred = $q.defer();

    if (currentUser.advanced_permissions) {
      redirect(configurationURL.advancedPermissions.baseUrl);
      deferred.reject();
    } else {
      deferred.resolve();
    }

    return deferred.promise;
  }

  /**
   * Checks if the accountToken is present and triggers the account switch
   *
   * @returns {Promise}
   * @private
   */
  function _checkAccountToken() {
    const deferred = $q.defer();
    const tr = $uiRouterGlobals.transition;
    const accountToken = $location.search().accountToken || null;

    if (accountToken) {
      Account.attemptSwitchUsingToken(accountToken).then(status => {
        const { name } = tr.to();
        const params = {
          ...tr.params(),
          accountToken: undefined,
        };

        const redirect = _getRedirectObject(
          {
            url: tr.router.stateService.href(name, params),
            reload: status === 202,
          },
          null,
          200,
        );

        deferred.reject(redirect);
      });
    } else {
      deferred.resolve();
    }

    return deferred.promise;
  }

  /**
   * Check if user can access account settings module
   *
   * @returns {Promise}
   * @private
   */
  function _canUseAccountSettingsAccess() {
    return _checkSettingsAccess(user => user.role === 'admin');
  }

  /**
   * Initialize history events
   *
   * @returns {Promise}
   * @private
   */
  function _initHistoryEvents() {
    return initHistoryEvents();
  }

  /**
   * Checks if user can access settings for specific set of roles
   *
   * @param {Function} condition
   * @returns {Promise<RejectionRedirect>|User}
   * @private
   */
  function _checkSettingsAccess(condition) {
    return $q.all([User.getCurrent(), Account.getNewTermsStatus()]).then(result => {
      const [currentUser, termsStatus] = result;

      if (!currentUser.super_admin && some(termsStatus, _missingTerms)) {
        return $q.reject(_redirectWithAccessDenied('terms'));
      }

      if (!condition(currentUser)) {
        return $q.reject(_redirectWithAccessDenied('apps'));
      }

      return currentUser;
    });
  }

  /**
   * Checks if user should be forced to the terms page
   *
   * @param {TermStatus} status
   * @returns {Boolean}
   * @private
   */
  function _missingTerms(status) {
    if (!status) {
      return false;
    }

    return status.deadline_reached && !status.accepted;
  }

  /**
   * @param {Object} result
   * @returns {Promise}
   * @private
   */
  function _canAccessPaymentSetup(result) {
    return Account.getCurrent().then(currentAccount => {
      if (currentAccount.payment_method !== 'credit card') {
        return $q.reject(_redirectWithAccessDenied('accountSettings.account-data'));
      }

      return result.data;
    });
  }

  /**
   * @param {Object} result
   * @returns {Promise}
   * @private
   */
  function _canAccessUsersRoleSelection(result) {
    return Account.getCurrent().then(currentAccount => {
      if (Account.hasAdvancedPermissionsPackage(currentAccount)) {
        return result.data;
      }

      return $q.reject(_redirectWithAccessDenied('accountSettings.users.create-user'));
    });
  }

  /**
   * @param {Object} result
   * @returns {Promise}
   * @private
   */
  function _canAccessSsoConfig(result) {
    return Account.getCurrent().then(currentAccount => {
      if (!Account.hasSecurityPackage(currentAccount)) {
        return $q.reject(_redirectWithAccessDenied('accountSettings.account-data'));
      }

      return result.data;
    });
  }

  /**
   * Run all tasks sequentially defined as resolve requirements
   *
   * @param {Array} resolve - array of requirements to resolve
   * @param {Object=} data - optional data coming from previous requirement
   * @param {Object=} params - optional parameters required for some tasks to be run
   * @returns {Promise} A promise that represent the state requirements resolving
   * @private
   */
  function _runTasks(resolve, data, params) {
    var promise;

    each(resolve, function (methodName) {
      var task = _tasks[methodName];

      if (promise) {
        promise = promise.then(function (data) {
          return task({ data: data, params: params });
        });
      } else {
        promise = task({ data: data, params: params });
      }
    });

    if (!promise) {
      promise = $q.when(data);
    }

    return promise;
  }

  /**
   * Form the redirect object
   *
   * @param {String} redirect - state to redirect to
   * @param {String} message - error message
   * @param {Number} status - error status
   * @returns {Object} redirect object
   * @private
   */
  function _getRedirectObject(redirect, message, status) {
    return {
      redirect,
      message,
      status,
    };
  }

  /**
   * @param {String} state
   * @returns {Redirect}
   * @private
   */
  function _redirectWithAccessDenied(nextState) {
    const redirectionProps = nextState?.startsWith('accountSettings')
      ? { nextState }
      : { url: '/' + $state.href(nextState) };

    return _getRedirectObject(redirectionProps, 'access-denied', 401);
  }

  /**
   * @returns {Redirect}
   * @private
   */
  function _redirectToLogin() {
    const loginUrl = $state.href('login.form', {
      redirectUrl: _buildRedirectUrl($uiRouterGlobals.transition),
    });

    return _getRedirectObject({ url: '/' + loginUrl }, 'access-denied', 401);
  }

  /**
   * @param {Transition} tr
   * @returns {String}
   * @private
   */
  function _buildRedirectUrl(tr) {
    const accountToken = $location.search().accountToken || null;
    const url = $state.href(tr.to().name, tr.params('to'));

    if (accountToken) {
      const [path, prevParams] = url.split('?');
      const params = (prevParams ? prevParams + '&' : '') + `accountToken=${accountToken}`;
      return path + '?' + params;
    }

    return url;
  }

  /**
   * Resolve the given route with optional resolved data and parameters
   *
   * @param {String} route - route to resolve
   * @param {Object=} data - optional previously resolved data
   * @param {Object=} params - optional params
   * @returns {Promise} A promise representing the state of route resolving requirements
   */
  function resolve(route, data, params) {
    var routes = route.split('.');
    var resource;

    each(routes, function (subRoute) {
      resource = resource ? resource[subRoute] : _resources[subRoute];
    });

    return _runTasks(resource.resolve, data, params);
  }
}

export { RouteResolver };
